import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { PackagingLabelSize } from "@enums/packagingLabelSize.enum";
import { CheckGroupPermissions } from "@helpers/checkGroupPermissions";
import { AddShippingReferenceNumberToShippingNote } from "@interfaces/addShippingReferenceNumberToShippingNote.interface";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { ChangeQuantity } from "@interfaces/changeQuantity.interface";
import { Client } from "@interfaces/models/client.interface";
import { ClientLocation } from "@interfaces/clientLocation.interface";
import { DownloadShippingNoteLabels } from "@interfaces/downloadShippingNoteLabels.interface";
import { EdiShippingNote } from "@interfaces/models/ediShippingNote.interface";
import { EdiShippingNoteItem } from "@interfaces/models/ediShippingNoteItem.interface";
import { SalesOrder } from "@interfaces/salesOrder.interface";
import { AppSettingsService } from "@services/appSettings.service";
import { ClientService } from "@services/client.service";
import { ClientLocationService } from "@services/clientLocation.service";
import { EdiShippingNoteService } from "@services/ediShippingNote.service";
import { EdiShippingNoteItemService } from "@services/ediShippingNoteItem.service";
import { SalesOrderService } from "@services/salesOrder.service";
import { UserProfileService } from "@services/userProfile.service";
import { ToastService } from "angular-toastify";
import { Guid } from "guid-typescript";

@Component({
	moduleId: module.id,
	selector: "ediShippingNotes-id",
	styleUrls: ["ediShippingNotes-id.css"],
	templateUrl: "ediShippingNotes-id.html"
})
export class EdiShippingNotesIdAdminComponent {
	asnEditingBlocked = true;
	client: Client = new Client();
	clientLocation: ClientLocation = new ClientLocation();
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerSecondaryButtons: AdminHeaderButton[] = [];
	headerTertiaryButtons: AdminHeaderButton[] = [];
	isSuperAdmin = false;
	item: EdiShippingNote = new EdiShippingNote();
	itemType = "Shipping Note";
	labelSizes: PackagingLabelSize[] = [];
	mode = "view";
	popupAddButtonOptions: any;
	popupAddShippingReferenceNumberToShippingNote: AddShippingReferenceNumberToShippingNote = new AddShippingReferenceNumberToShippingNote();
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupLineItem: EdiShippingNoteItem = new EdiShippingNoteItem();
	popupTitle = "";
	popupVisible = false;
	readOnly = true;
	returnUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	salesOrder: SalesOrder = new SalesOrder();
	shippingNoteItems: any[] = [];
	statuses: any;
	title: string = "View " + this.itemType;
	userProfiles: any[] = [];

	constructor(private router: Router, private toastService: ToastService, private route: ActivatedRoute, private appSettingsService: AppSettingsService, private itemService: EdiShippingNoteService, private clientLocationService: ClientLocationService, private clientService: ClientService, private shippingNoteItemService: EdiShippingNoteItemService, private salesOrderService: SalesOrderService, private userProfileService: UserProfileService) {
		this.isSuperAdmin = CheckGroupPermissions(appSettingsService, "Super Admin");
		this.getUserProfiles();
		this.route.params
			.subscribe((params) => {
				if (params.id != "create") {
					if (params.id.split("_").length > 1) {
						this.getItem(params.id.split("_")[0], "edit");
						this.edit();
					} else {
						this.getItem(params.id, "view");
					}
				}
			});

		this.editLineItemPopup = this.editLineItemPopup.bind(this);
		this.isSplitShippingNoteItemAvailable = this.isSplitShippingNoteItemAvailable.bind(this);
		this.isViewSplitIntoShippingNoteAvailable = this.isViewSplitIntoShippingNoteAvailable.bind(this);
		this.splitItemIntoShippingNote = this.splitItemIntoShippingNote.bind(this);
		this.viewParentShippingNote = this.viewParentShippingNote.bind(this);
		this.viewSalesOrder = this.viewSalesOrder.bind(this);
		this.viewSplitOffShippingNote = this.viewSplitOffShippingNote.bind(this);
	}

	addShippingReferenceNumber() {
		this.popupAddShippingReferenceNumberToShippingNote.id = this.item.id;
		this.itemService.addShippingReferenceNumberToLockedNote(this.popupAddShippingReferenceNumberToShippingNote)
			.subscribe(
				() => {
					this.getItem(this.item.lookupId, this.mode);
					this.closePopup();
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}

	addShippingReferenceNumberPopup() {
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.addShippingReferenceNumber(), text: "Save Changes" };
		this.popupCloseButtonOptions = { onClick: () => this.closePopup(), text: "Close" };
		this.popupAddShippingReferenceNumberToShippingNote = new AddShippingReferenceNumberToShippingNote();
		this.popupTitle = "Add Shipping Reference Number";
	}

	buildButtons(mode: string) {
		this.mode = mode;
		this.headerPrimaryButtons = [];
		this.headerSecondaryButtons = [];
		this.headerTertiaryButtons = [];	

		if (mode == "edit") {
			if (this.item.isLocked == false) {
				this.headerPrimaryButtons.push({ method: "updateItem", text: "Save Changes" });
			}
			this.headerPrimaryButtons.push({ method: "cancelEditing", text: "Cancel" });
			this.headerPrimaryButtons.push({ method: "close", text: "Close" });
		} else if (mode == "view") {
			if (this.item.isLocked === false) {
				this.headerPrimaryButtons.push({ method: "edit", text: "Edit" });
				this.headerTertiaryButtons.push({ method: "lock", text: "Lock Details" });
			} else {
				if (this.labelSizes.includes(PackagingLabelSize.Small)) {
					this.headerTertiaryButtons.push({ method: "generateSmallLabelsPDF", text: "Small Labels" });
				}
				if (this.labelSizes.includes(PackagingLabelSize.Standard)) {
					this.headerTertiaryButtons.push({ method: "generateStandardLabelsPDF", text: "Standard Labels" });
				}
				if (this.item.totalPallets >= 1) {
					this.headerTertiaryButtons.push({ method: "generateMasterLabels", text: "Pallet Labels" });
				}
				if (this.item.clientLocationId === 2) { // Location code for NITRA
					this.headerTertiaryButtons.push({ method: "generateNitraExportDocumentPDF", text: "Export Document" });
				}
				if (this.item.isSent == false) {
					if (this.item.shippingReferenceNumber === undefined || this.item.shippingReferenceNumber === null || this.item.shippingReferenceNumber === "") {
						this.headerSecondaryButtons.push({ method: "addShippingReferenceNumberPopup", text: "Add Shipping Reference Number" });
					} else {
						if (this.client.sendASNsViaEDI === true) {
							this.headerSecondaryButtons.push({ method: "sendAsn", text: "Send ASN" });
						}
						else
						{
							this.headerSecondaryButtons.push({ method: "markSent", text: "Mark as Sent" });
						}
					}
					if (this.isSuperAdmin === true) {
						this.headerSecondaryButtons.push({ method: "unlock", text: "Unlock" });
					}
				}
				if (this.item.salesOrderId !== undefined && this.item.salesOrderId !== null) {
					this.headerSecondaryButtons.push({ method: "viewSalesOrder", text: "View Sales Order" });
				}
			}
			if (this.item.parentEdiShippingNoteId !== undefined && this.item.parentEdiShippingNoteId !== null) {
				this.headerSecondaryButtons.push({ method: "viewParentShippingNote", text: "View Parent Note" });
			}
			this.headerPrimaryButtons.push({ method: "close", text: "Close" });
		}
	}

	cancelEditing() {
		if (confirm("Are you sure you wish to switch modes? Unsaved changes will be discarded.") == false) {
			return;
		}
		this.readOnly = true;
		this.buildButtons("view");
		this.title = "View " + this.itemType;
	}

	close() {
		this.router.navigate([`/${this.returnUrl}/`]);
	}

	closePopup() {
		this.popupVisible = false;
	}

	edit() {
		this.readOnly = false;
		this.buildButtons("edit");
		this.title = "Edit " + this.itemType;
		this.toastService.warn("You are now editing...");
	}

	editLineItemPopup(e: any) {
		if (this.item === null || this.item === undefined || this.item.isLocked === true) {
			return;
		}

		this.shippingNoteItemService.getSingleById(e.row.data.id).subscribe(
			(res: any) => {
				this.popupVisible = true;
				this.popupAddButtonOptions = { onClick: () => this.updateLineItem(), text: "Save Changes" };
				this.popupCloseButtonOptions = { onClick: () => this.closePopup(), text: "Close" };
				this.popupLineItem = res.response;
				this.popupTitle = "Edit Line Item";
			},
			(err) => {
				console.log(err);
				this.toastService.error("Could not edit line item");
			}
		);
	}

	generateLabelsPDF(labelSize: PackagingLabelSize) {
		const values = new DownloadShippingNoteLabels();
		values.shippingNoteId = this.item.id;
		values.labelSize = labelSize;
		this.itemService.generateLabels(values).subscribe(
			(response: any) =>{
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
				downloadLink.setAttribute("download", "ShippingNote_" + this.item.id + "_" + PackagingLabelSize[labelSize] + "_Labels.pdf");
				document.body.appendChild(downloadLink);
				downloadLink.click();
			}
		);
	}

	generateMasterLabels(){
		this.generateMasterLabelsPDF();
	}

	generateMasterLabelsPDF() {
		this.itemService.generateMasterLabels(this.item.id).subscribe(
			(response: any) =>{
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
				downloadLink.setAttribute("download", "ShippingNotePallets_" + this.item.id + "_Labels.pdf");
				document.body.appendChild(downloadLink);
				downloadLink.click();
			}
		);
	}

	generateNitraExportDocumentPDF() {
		this.itemService.generateNitraExportDocument(this.item.id).subscribe(
			(response: any) =>{
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
				downloadLink.setAttribute("download", "ShippingNotePallets_" + this.item.id + "_Export_Document.pdf");
				document.body.appendChild(downloadLink);
				downloadLink.click();
			}
		);
	}

	generateSmallLabelsPDF() {
		this.generateLabelsPDF(PackagingLabelSize.Small);
	}

	generateStandardLabelsPDF() {
		this.generateLabelsPDF(PackagingLabelSize.Standard);
	}

	getClient(id: number, buttonMode: string) {
		this.clientService.getSingleById(id)
			.subscribe(
				(res: any) => {
					this.client = res.response;
					this.asnEditingBlocked = this.client.sendASNsViaEDI;
					if (this.item.isLocked) {
						this.getLabelSizes(this.item.id);
					} else {
						this.buildButtons(buttonMode);
					}
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}

	getClientLocation(id: number, buttonMode: string) {
		this.clientLocationService.getSingleById(id)
			.subscribe(
				(res: any) => {
					this.clientLocation = res.response;
					this.getClient(this.clientLocation.clientId, buttonMode);
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}

	getItem(itemId: Guid, buttonMode: string) {
		this.itemService.getSingleByLookupId(itemId)
			.subscribe(
				(res: any) => {
					this.item = res.response;
					this.getSalesOrder();
					this.getShippingNoteItems(this.item.id);
					this.getClientLocation(this.item.clientLocationId, buttonMode);
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}

	getLabelSizes(id: number) {
		this.itemService.getLabelSizes(id)
			.subscribe(
				(res: any) => {
					this.labelSizes = res.response;
					this.buildButtons("view");
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}

	getSalesOrder() {
		if (this.item.salesOrderId === null || this.item.salesOrderId === undefined) {
			return;
		}
		this.salesOrderService.getSingleById(this.item.salesOrderId)
			.subscribe(
				(res: any) => {
					this.salesOrder = res.response;
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}

	getShippingNoteItems(id: number) {
		this.shippingNoteItemService.getItemsForShippingNote(id)
			.subscribe(
				(res: any) => {
					this.shippingNoteItems = res.response;
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}

	// Active user profiles — used to resolve the accountability ids (created/locked/sent) to names.
	getUserProfiles() {
		this.userProfileService.getActiveUsers()
			.subscribe(
				(res: any) => this.userProfiles = res.response,
				(err) => console.log(err)
			);
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	isSplitShippingNoteItemAvailable(e: any) {
		return (this.mode === "view" && this.item.isSent === true && (this.item.parentEdiShippingNoteId === undefined || this.item.parentEdiShippingNoteId === null) && e.row.data.ediOrder.orderedQuantity > e.row.data.quantity && (e.row.data.splitIntoEdiShippingNoteId === undefined || e.row.data.splitIntoEdiShippingNoteId === null));
	}

	isViewSplitIntoShippingNoteAvailable(e: any) {
		return (this.mode === "view" && e.row.data.splitIntoEdiShippingNoteId !== undefined && e.row.data.splitIntoEdiShippingNoteId !== null && e.row.data.splitIntoEdiShippingNoteId > 0);
	}

	lock() {
		if (confirm("Are you sure you wish to lock this shipping note and generate the ASN Number? You will be unable to make further changes.") == false) {
			return;
		}

		this.itemService.lockShippingNote(this.item.id)
			.subscribe(
				() => {
					this.toastService.success("Shipping Note Locked");
					this.getItem(this.item.lookupId, "view");
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}

	markSent() {
		if (confirm("Are you sure you wish to mark this shipping note as sent? This cannot be undone.") == false) {
			return;
		}
		
		this.itemService.markSent(this.item.id)
			.subscribe(
				() => {
					this.toastService.success("Marked as sent");
					this.getItem(this.item.lookupId, "view");
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}
	
	onLineItemRowPrepared(e: any) {
		if (e.rowType === "data") {  
			if (e.data.quantity !== e.data.ediOrder.orderedQuantity) {
				e.rowElement.classList.add("quantityMisMatch");
				e.rowElement.style.backgroundColor = "#ffd2ad";  
				e.rowElement.className = e.rowElement.className.replace("dx-row-alt", "");
			}
		}  
	}

	sendAsn() {
		if (confirm("Are you sure you wish to generate and send the ASN for this shipping note? This cannot be undone.") == false) {
			return;
		}
		
		this.itemService.sendMessage(this.item.id)
			.subscribe(
				() => {
					this.toastService.success("Message Queued");
					this.getItem(this.item.lookupId, "view");
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}

	splitItemIntoShippingNote(e: any) {
		if ((e.row.data.splitIntoEdiShippingNoteId !== undefined && e.row.data.splitIntoEdiShippingNoteId !== null) || this.item.isSent === false || (this.item.parentEdiShippingNoteId !== undefined && this.item.parentEdiShippingNoteId !== null)) {
			return;
		}
		if (confirm("Are you sure you wish to create a new shipping note for the shortfall?") == false) {
			return;
		}
		this.itemService.splitFromEdiShippingNoteItem(e.row.data.id)
			.subscribe(
				(res: any) => {
					const lookupId = res.response.lookupId;
					this.router.navigate([`/${this.returnUrl}/${lookupId}`]);
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);		
	}

	unlock() {
		if (confirm("Are you sure you wish to unlock this shipping note? This will invalidate all current labels and will delete the associated Sales Order, Invoice and Delivery Note.") == false) {
			return;
		}

		this.itemService.unlockShippingNote(this.item.id)
			.subscribe(
				() => {
					this.toastService.success("Shipping Note Unlocked");
					this.getItem(this.item.lookupId, "view");
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}

	updateItem() {
		if (this.item.isLocked) {
			this.toastService.error("Cannot change locked Shipping Notes");
			return;
		}

		this.itemService.updateSingleById(this.item.id, this.item)
			.subscribe(
				() => {
					this.toastService.success("Successfully Updated " + this.itemType);
					this.cancelEditing();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	updateLineItem() {
		if (this.item === null || this.item === undefined || this.item.isLocked === true) {
			return;
		}

		const newQuantity = new ChangeQuantity();
		newQuantity.quantity = this.popupLineItem.quantity;

		this.shippingNoteItemService.changeQuantity(this.popupLineItem.id, newQuantity)
			.subscribe(
				() => {
					this.toastService.success("Successfully Updated Line Item");					
					this.closePopup();
					this.getShippingNoteItems(this.item.id);
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	viewParentShippingNote() {
		if (this.item.parentEdiShippingNoteId === undefined || this.item.parentEdiShippingNoteId === null) {
			return;
		}
		this.itemService.getSingleById(this.item.parentEdiShippingNoteId)
			.subscribe(
				(res: any) => {
					const lookupId = res.response.lookupId;
					this.router.navigate([`/${this.returnUrl}/${lookupId}`]);
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);		
	}

	viewSalesOrder() {
		this.router.navigate([`admin/salesOrders/${this.salesOrder.lookupId}`]);
	}

	viewSplitOffShippingNote(e: any) {
		const splitShippingNoteId = e.row.data.splitIntoEdiShippingNoteId;
		if (splitShippingNoteId === undefined || splitShippingNoteId === null) {
			return;
		}
		this.itemService.getSingleById(splitShippingNoteId)
			.subscribe(
				(res: any) => {
					const lookupId = res.response.lookupId;
					this.router.navigate([`/${this.returnUrl}/${lookupId}`]);
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);		
	}
}